import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Send, Info, Check, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SURVEY_QUESTIONS, SURVEY_CLOSED, getVisibleQuestions } from "../config/surveyConfig";
import API from "../lib/api";

const DRAFT_KEY = "survey_draft";
const STEP_KEY = "survey_step";
const ENCOURAGEMENTS = [
  "Great progress!",
  "Keep going!",
  "Almost there!",
  "You're doing well!",
  "Nearly done!",
  "Just a few more!",
];

export default function SurveyPage() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    return saved ? JSON.parse(saved) : {};
  });
  const [step, setStep] = useState(() => {
    const saved = localStorage.getItem(STEP_KEY);
    return saved ? parseInt(saved, 10) || 0 : 0;
  });
  const [submitting, setSubmitting] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [animDir, setAnimDir] = useState("right");

  const userData = useMemo(() => {
    // Check sessionStorage first, then localStorage for resume
    let raw = sessionStorage.getItem("survey_user");
    if (!raw) {
      raw = localStorage.getItem("survey_user");
      if (raw) sessionStorage.setItem("survey_user", raw);
    }
    return raw ? JSON.parse(raw) : null;
  }, []);

  useEffect(() => {
    if (SURVEY_CLOSED || !userData) navigate("/", { replace: true });
  }, [userData, navigate]);

  const questions = useMemo(() => getVisibleQuestions(answers), [answers]);

  // Save draft and step
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(answers));
  }, [answers]);
  useEffect(() => {
    localStorage.setItem(STEP_KEY, String(step));
  }, [step]);

  const current = questions[step];
  const progress = questions.length > 0 ? ((step + 1) / questions.length) * 100 : 0;

  const setAnswer = useCallback((qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
  }, []);

  const handleSingle = (qid, option) => {
    setAnswer(qid, option);
  };

  const handleMulti = (qid, option, exclusive) => {
    setAnswers(prev => {
      const current = prev[qid] || [];
      if (exclusive && exclusive.includes(option)) {
        return { ...prev, [qid]: [option] };
      }
      const filtered = current.filter(o => !exclusive?.includes(o));
      if (filtered.includes(option)) {
        return { ...prev, [qid]: filtered.filter(o => o !== option) };
      }
      const newList = [...filtered, option];
      const q = SURVEY_QUESTIONS.find(q => q.id === qid);
      if (q?.maxSelections && newList.length > q.maxSelections) {
        toast.info(`You can pick up to ${q.maxSelections} — choose your favorites!`);
        return prev;
      }
      return { ...prev, [qid]: newList };
    });
  };

  const canProceed = () => {
    if (!current) return false;
    if (current.type === "text") return true;
    if (!current.required) return true;
    const val = answers[current.id];
    if (current.type === "single") return !!val;
    if (current.type === "multi") return Array.isArray(val) && val.length > 0;
    return true;
  };

  const handleNext = () => {
    if (current?.type === "single" && current?.id === "q_tool_most_used" && answers[current.id] === "Others" && otherText.trim()) {
      setAnswer(current.id, `Others: ${otherText.trim()}`);
    }
    if (step < questions.length - 1) {
      setAnimDir("right");
      setStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setAnimDir("left");
      setStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!userData) return;
    setSubmitting(true);
    try {
      const session = localStorage.getItem("survey_session");
      let sessionData = {};
      if (session) {
        try { sessionData = JSON.parse(session); } catch {}
      }
      const { data } = await API.post("/survey/submit", {
        short_name: userData.short_name,
        email: userData.email,
        answers,
        session_id: sessionData.session_id || null,
        started_at: sessionData.started_at || null
      });
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(STEP_KEY);
      localStorage.removeItem("survey_user");
      localStorage.removeItem("survey_session");
      sessionStorage.setItem("survey_result", JSON.stringify(data));
      navigate("/thank-you");
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.response?.data?.error || "Failed to submit survey. Please try again.";
      toast.error(errorMsg);
      console.error("Survey submit error:", e.response?.data || e);
    } finally {
      setSubmitting(false);
    }
  };

  if (!current || !userData) return null;
  const isLast = step === questions.length - 1;
  const encouragement = ENCOURAGEMENTS[step % ENCOURAGEMENTS.length];
  const showEncouragement = step > 0 && step % 4 === 0;

  return (
    <div className="min-h-screen bg-[#fdf7ff] flex flex-col relative overflow-hidden" data-testid="survey-page">
      {/* Decorative bg */}
      <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full bg-[#f5d0fe]/20 blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-48 h-48 rounded-full bg-[#a21caf]/5 blur-2xl" />

      {/* Progress bar */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b-2 border-[#111]" data-testid="progress-bar">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">{current.emoji || ""}</span>
            <span className="text-xs font-bold text-[#a21caf] whitespace-nowrap">
              {step + 1} of {questions.length}
            </span>
          </div>
          <div className="flex-1 h-3 rounded-full border-2 border-[#111] bg-white overflow-hidden">
            <div
              className="h-full rounded-full progress-fill"
              style={{
                width: `${progress}%`,
                background: `linear-gradient(90deg, #a21caf, #d946ef)`,
              }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-500">{Math.round(progress)}%</span>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-8 relative z-10">
        <div className="w-full max-w-2xl" key={step}>
          {/* Encouragement toast */}
          {showEncouragement && (
            <div className="flex items-center gap-2 mb-4 justify-center animate-slide-up">
              <Sparkles className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
              <span className="text-sm font-semibold text-[#a21caf]">{encouragement}</span>
            </div>
          )}

          {/* Helper note for conditional questions */}
          {current.helperNote && (
            <div className="flex items-center gap-2 mb-4 px-4 py-2.5 rounded-xl bg-[#f5d0fe]/30 border-2 border-[#a21caf]/20 animate-slide-up" data-testid="helper-note">
              <Info className="w-4 h-4 text-[#a21caf] flex-shrink-0" strokeWidth={2.5} />
              <span className="text-sm text-[#a21caf] font-medium">{current.helperNote}</span>
            </div>
          )}

          {/* Question card */}
          <div className="nb-card mb-6 animate-slide-up" data-testid={`question-${current.id}`}>
            <div className="flex items-start gap-3 mb-4">
              <span className="text-3xl">{current.emoji || ""}</span>
              <div className="flex-1">
                <h2 className="font-parkinsans text-xl sm:text-2xl text-[#111] leading-snug">
                  {current.question}
                </h2>
                {current.helper && (
                  <p className="text-sm text-gray-500 mt-2">{current.helper}</p>
                )}
              </div>
            </div>
            {current.optional && !current.helper?.includes("Optional") && (
              <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-[#fdf7ff] border border-gray-200">
                <span className="text-xs text-gray-400 italic">Optional — feel free to skip.</span>
              </div>
            )}

            {/* Single choice */}
            {current.type === "single" && (
              <div className="space-y-2.5" data-testid="options-single">
                {current.options.map(opt => (
                  <button
                    key={opt}
                    onClick={() => handleSingle(current.id, opt)}
                    className={`nb-option ${answers[current.id] === opt ? "selected" : ""}`}
                    data-testid={`option-${opt.slice(0, 30).replace(/\s/g, '-').toLowerCase()}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        answers[current.id] === opt
                          ? "bg-white border-white"
                          : "bg-white border-[#111]"
                      }`}>
                        {answers[current.id] === opt && <Check className="w-3 h-3 text-[#a21caf]" strokeWidth={3} />}
                      </span>
                      <span className="text-left">{opt}</span>
                    </span>
                  </button>
                ))}
                {current.hasOtherField && answers[current.id] === "Others" && (
                  <input
                    type="text"
                    placeholder="Tell us which tool..."
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    className="nb-input mt-2"
                    data-testid="other-text-input"
                  />
                )}
              </div>
            )}

            {/* Multi select */}
            {current.type === "multi" && (
              <div className="space-y-2.5" data-testid="options-multi">
                {current.maxSelections && (
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#a21caf] transition-all"
                        style={{ width: `${((answers[current.id] || []).length / current.maxSelections) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#a21caf] font-bold">
                      {(answers[current.id] || []).length}/{current.maxSelections}
                    </span>
                  </div>
                )}
                {current.options.map(opt => {
                  const selected = (answers[current.id] || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      onClick={() => handleMulti(current.id, opt, current.exclusive)}
                      className={`nb-option-multi ${selected ? "selected" : ""}`}
                      data-testid={`option-${opt.slice(0, 30).replace(/\s/g, '-').toLowerCase()}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          selected ? "bg-[#a21caf] border-[#a21caf]" : "bg-white border-[#111]"
                        }`}>
                          {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </span>
                        <span className="text-left">{opt}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Long text */}
            {current.type === "text" && (
              <textarea
                rows={4}
                placeholder="Share your thoughts here..."
                value={answers[current.id] || ""}
                onChange={(e) => setAnswer(current.id, e.target.value)}
                className="nb-input resize-none"
                data-testid="text-answer"
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4" data-testid="survey-nav">
            <button
              onClick={handleBack}
              disabled={step === 0}
              className={`nb-btn-secondary ${step === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back
            </button>

            {isLast ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`nb-btn-primary ${submitting ? "opacity-70" : ""}`}
                data-testid="submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                ) : (
                  <>Finish & see my results <Send className="w-4 h-4" strokeWidth={2.5} /></>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`nb-btn-primary ${!canProceed() ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="next-btn"
              >
                {current.type === "text" || !current.required ? "Skip / Next" : "Next"} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
